// Provides audit log utility functions
import { titleCase } from "./strings";
import { formatDate } from "./datetime";

import { displayUnitType } from "./unitType";
import { billingAccountStatusText } from "./billingAccount";

const ignoredKeys = [
  "updated_at",
  "inserted_at",
  "updated_by",
  "text_search_english",
  "configuration_discrepancy",
  "bypass_audit_log",
  "provisioning_status",
  "target_configuration_profile_id",
];

const updateKeys = ["updated_at", "updated_by"];
const duplicateLogsKey = [
  "starts_at",
  "ends_at",
  "unit_enrollment_mode",
  "alert_resumption_margin",
  "late_customer_margin",
  "disabled",
  "sensitivity",
  "spots",
  "mute_reason",
  "status",
  "phone_number",
  "muted_until",
];

function formatOperationType(log) {
  const operation = log?.operation;
  switch (operation) {
    case 0:
      return "added";
    case 1:
      return "updated";
    case 2:
      return "deleted";
    default:
      break;
  }
}

export function formatLogDescription(log) {
  let operation = formatOperationType(log);

  const keys = Object.keys(log?.changes || {});

  const hasOnlyIgnoredKeys =
    keys.length > 0 && keys.every((key) => ignoredKeys.includes(key));

  if (hasOnlyIgnoredKeys) {
    return null;
  }

  switch (log?.entity) {
    case "billing_accounts":
      return formatBillingAccount(log, operation);

    case "billing_account_organization_assignments":
      return formatBillingAccountOrganizationAssignment(log, operation);

    case "contact_methods":
      return formatContactMethod(log, operation);

    case "unit_types":
      return formatUnitType(log, operation);

    case "device_assignments":
      return formatDeviceAssignment(log, operation);

    case "devices":
      return formatDevice(log, operation);

    case "organizations":
      return formatOrganization(log, operation);

    case "organization_details":
      return formatOrganizationDetail(log, operation);

    default:
      break;
  }
  return `${titleCase(operation)} ${titleCase(log?.entity)}`;
}

function customizeLogDescription(log) {
  if (log?.primary_zone_change) {
    return `Primary zone changed from ${log.primary_zone_change}`;
  }
  if (log?.unit_type_change) {
    return `Unit type has been changed from ${log.unit_type_change}`;
  }
  return formatChangeObjectToDescription(log?.changes, [
    ...ignoredKeys,
    "path",
  ]);
}

function formatOrganization(log, operation) {
  const { organizationType, organizationSubtype, organizationName } = log;
  const description = customizeLogDescription(log);

  if (organizationType === "zone") {
    const name = titleCase(organizationName);
    const subtype = titleCase(organizationSubtype);
    const capitalizedOperation = titleCase(operation);

    switch (operation) {
      case "added":
        return `${subtype} ${name} has been added`;
      case "updated":
        const changes = description ? `: ${description}` : "with no changes";
        return `${capitalizedOperation} ${subtype} ${name} ${changes}`;
      default:
        return description;
    }
  }

  return description;
}

function formatOrganizationDetail(log, operation) {
  const description = formatChangeObjectToDescription(
    operation === "added" ? jsonDiff(log?.data, {}) : log?.changes,
    [...ignoredKeys, "organization_id"]
  );

  return `${titleCase(operation)} ${
    log?.organizationType === "zone"
      ? `${log?.organizationSubtype} ${titleCase(
          log?.organizationName
        )} details with ${
          description === "" ? "no changes" : `: ${description}`
        }`
      : ""
  }`;
}

function formatBillingAccount(log, operation) {
  operation =
    operation === "added"
      ? `${titleCase(log?.name?.split("@")[0])} has been added`
      : operation === "updated"
      ? `${titleCase(
          log?.name?.split("@")[0]
        )} has been updated: ${billingAccountStatusText(log?.status)}`
      : `${titleCase(log?.name?.split("@")[0])} has been deleted`;

  return `${operation} ${
    log?.organizationType === "zone"
      ? `${titleCase(log?.organizationSubtype)} ${log?.organizationName}`
      : ""
  }`;
}

function formatBillingAccountOrganizationAssignment(log, operation) {
  if (operation === "added") {
    return `${titleCase(log?.name?.split("@")[0])} has been ${
      log?.organizationType === "zone"
        ? ` assigned to ${titleCase(log?.organizationSubtype)} ${
            log?.organizationName
          }`
        : "added"
    }`;
  }

  if (
    (operation === "updated" && log?.data?.unassigned_at) ||
    operation === "deleted"
  ) {
    return `${titleCase(log?.name?.split("@")[0])} has been ${
      log?.organizationType === "zone"
        ? ` unassigned from ${titleCase(log?.organizationSubtype)} ${
            log?.organizationName
          }`
        : "removed"
    }`;
  }

  return `${log?.organizationType === "facility" ? "Added" : "Assigned"} ${
    log?.organizationType === "zone"
      ? `${titleCase(log?.organizationSubtype)} ${log?.organizationName}`
      : ""
  }`;
}

function formatContactMethod(log, operation) {
  const description = `${titleCase(log?.name)}'s ${
    log?.data?.is_default ? "primary " : ""
  }${log?.data?.type} has been ${
    operation === "added"
      ? "added"
      : operation === "updated"
      ? "updated from"
      : "removed"
  }`;

  const changes = `${
    operation === "added"
      ? log?.data?.value
      : `${log?.changes?.value?.old || log?.data?.value} to ${
          log?.changes?.value?.new || log?.data?.value
        }`
  }`;

  return `${description} ${changes}`;
}

function formatUnitType(log, operation) {
  const { oldData, newData } = mergeDataWithChanges(log);

  const description = `${
    operation === "added" ? displayUnitType(newData) : displayUnitType(oldData)
  } has been ${operation}`;

  const changes = `${
    operation === "updated" ? `to ${displayUnitType(newData)}` : ""
  }`;

  return `Unit type ${description} ${changes}`;
}

function formatDevice(log, operation) {
  let disposition = log?.changes?.disposition?.new ?? log?.data?.disposition;

  operation = `Device ${log?.data?.type?.toUpperCase()}-${
    log?.data?.short_id
  } has been ${
    operation === "added"
      ? "added"
      : `marked as ${titleCase(
          disposition == "in_service" ? "found" : disposition
        )}`
  }`;

  return operation;
}

function formatDeviceAssignment(log, operation) {
  operation =
    operation === "added"
      ? `Device ${log?.data?.short_id} has been ${
          log?.organizationType === "facility" ? "moved" : "added"
        } to`
      : (operation === "updated" && log?.data?.unassigned_at) ||
        operation === "deleted"
      ? `Device ${log?.data?.short_id} has been removed from`
      : `Device ${log?.data?.short_id} has been ${
          log?.organizationType === "facility" ? "moved" : "added"
        } to`;

  return `${operation} ${
    log?.organizationType === "zone"
      ? `${titleCase(log?.organizationSubtype)} ${log?.organizationName}`
      : "inventory"
  }`;
}

function formatChangeObjectToDescription(changes, ignoreKeys = []) {
  let description = "";
  for (const key in changes) {
    if (changes.hasOwnProperty(key) && !ignoreKeys.includes(key)) {
      const change = changes[key];
      if (
        (typeof change.old === "object" || typeof change.new === "object") &&
        (change.old !== null || change.new !== null) &&
        !Array.isArray(change.old) &&
        !Array.isArray(change.new)
      ) {
        description += formatChangeObjectToDescription(
          jsonDiff(change.new, change.old),
          ignoreKeys
        );
        continue;
      }

      if (checkEquality(change.old, change.new)) continue;

      description += `${titleCase(key)}- ${
        change.old
          ? `changed from ${formatValue(change.old)} to ${formatValue(
              change.new
            )}`
          : ` ${formatValue(change.new)} has been added`
      }\n`;
    }
  }
  return description;
}

function checkEquality(oldValue, newValue) {
  if (!oldValue && !newValue) return true;

  if (Array.isArray(oldValue) && Array.isArray(newValue)) {
    return oldValue.sort().join("") === newValue.sort().join("");
  }
  return oldValue === newValue;
}

function formatValue(value) {
  if (Array.isArray(value)) {
    return value
      .map((val) => {
        return titleCase(String(val));
      })
      .join(", ");
  }
  return value;
}

function jsonDiff(newObject, oldObject) {
  const result = {};

  newObject = newObject ?? {};
  oldObject = oldObject ?? {};
  if (typeof newObject !== "object" || typeof oldObject !== "object")
    return result;

  var keys = Object.keys(newObject).concat(Object.keys(oldObject));

  keys.forEach(function (key) {
    if (!(key in oldObject) || newObject[key] !== oldObject[key]) {
      result[key] = { old: oldObject[key], new: newObject[key] };
    }
  });

  return result;
}

function mergeDataWithChanges(log) {
  const newData = { ...log.data };
  const oldData = { ...log.data };

  for (const key in log.changes) {
    if (log.changes.hasOwnProperty(key)) {
      const change = log.changes[key];
      oldData[key] = change.old;
      newData[key] = change.new;
    }
  }

  return { oldData, newData };
}

// AuditLogs for BackOffice
export function formatLogDescriptionForBO(log) {
  if (!log) return null;

  const keys = Object.keys(log?.changes || {});

  const hasOnlyIgnoredKeys =
    keys.length > 0 && keys.every((key) => ignoredKeys.includes(key));

  if (hasOnlyIgnoredKeys) {
    return null;
  }

  let operation = formatOperationType(log);
  switch (log?.entity) {
    case "organizations":
      return formatOrganizationForBO(log, operation);
    case "organization_details":
      return formatOrganizationDetailForBO(log, operation);
    case "devices":
      return formatDeviceForBO(log, operation);
    case "device_assignments":
      return formatDeviceAssignmentForBO(log, operation);
    case "gateways":
      return formatGatewayForBO(log, operation);
    case "device_configurations_organizations":
      return formatDeviceConfigurationOrganizationForBO(log, operation);
    case "device_configuration_profiles":
      return formatDeviceConfigurationProfileForBO(log, operation);
    case "bulk_notifications":
      return formatBulkNotificationForBO(log, operation);
    case "shipment_logs":
      return formatShipmentLogForBO(log, operation);
  }
  return `${titleCase(operation)} ${titleCase(log?.entity)}`;
}
// Format Organization Details
function formatOrganizationDetailForBO(log, operation) {
  const keys = Object.keys(log?.changes || {});
  const facilityFqmChange = log?.changes?.facility_fqm;
  let description = [];
  if (facilityFqmChange && facilityFqmChange.new && facilityFqmChange.old) {
    const { new: newFqm, old: oldFqm } = facilityFqmChange;

    function pushFieldChange(field, label, formatFn = (v) => v) {
      const oldVal = oldFqm[field];
      const newVal = newFqm[field];
      if (newVal !== oldVal) {
        if (oldVal === null || oldVal === undefined || oldVal === "") {
          description.push(
            `${label} has been set to <green>${formatFn(newVal)}</green>`
          );
        } else if (newVal === null || newVal === undefined || newVal === "") {
          description.push(
            `${label} has been removed, previously was <red>${formatFn(
              oldVal
            )}</red>`
          );
        } else {
          description.push(
            `${label} changed from <red>${formatFn(
              oldVal
            )}</red> to <green>${formatFn(newVal)}</green>`
          );
        }
      }
    }
    pushFieldChange("install_date", "Install date", formatDate);
    pushFieldChange("training_date", "Training date", formatDate);
    pushFieldChange(
      "repair_priority",
      "Repair priority",
      (val) => val ?? "N/A"
    );
  }

  const hasOnlyUpdatedKeys = keys.every((key) => updateKeys.includes(key));
  if (hasOnlyUpdatedKeys) {
    return `${titleCase(operation)} ${titleCase(
      log?.organizationName
    )} details with no changes`;
  }

  const descString = description.join(", ");
  return [
    `${titleCase(operation)}${
      log?.organizationType === "facility"
        ? ` ${titleCase(log?.organizationName)} details with${
            descString === "" ? " no changes" : `: ${descString}`
          }`
        : ""
    }`,
  ];
}

function formatDeviceConfigurationOrganizationForBO(log, operation) {
  const organizationName = log?.organizationName || log?.facilityName || null;
  const profileName = log?.profile_name || "Unknown Profile";
  let description = [];

  if (organizationName) {
    description.push(
      `Device config ${profileName} has been added to <green>${organizationName}</green>`
    );
  }

  return description.length > 0
    ? description
    : [`${titleCase(operation)} device configuration organization`];
}

function formatDeviceConfigurationProfileForBO(log, operation) {
  const profileName = log?.data?.name || "Unknown Profile";
  const changes = log?.changes;

  let description = [];

  if (log?.data?.name && !changes) {
    description.push(
      `<green>${profileName}</green> device config has been added`
    );
  }

  // Check if heartbeat is turned off (null in the new profile)
  if (
    changes?.profile?.new?.heartbeat === null &&
    changes?.profile?.old?.heartbeat
  ) {
    description.push(
      `Heart beat has been <red>turned off</red> for "${profileName}" device configuration`
    );
  }

  if (
    changes?.profile?.old?.daytime?.day_hour_start !==
    changes?.profile?.new?.daytime?.day_hour_start
  ) {
    const oldDayStart = changes?.profile?.old?.daytime?.day_hour_start;
    const newDayStart = changes?.profile?.new?.daytime?.day_hour_start;

    description.push(
      `Day start has been updated from <red>"${oldDayStart}:00"</red> to <green>"${newDayStart}:00"</green> on "${profileName}" device configuration`
    );
  }

  return description.length > 0
    ? description
    : [`${titleCase(operation)} device configuration "${profileName}"`];
}

function formatGatewayForBO(log, operation) {
  const changes = log?.changes || {};
  const changed_organization_name = log?.changed_organization_name || {};
  const name = log?.data?.name || "Unknown";
  const operationValue = log?.operation;
  let description = [];

  if (operationValue === 0) {
    description.push(
      `Gateway ${name} has been assigned to <green>${log.organizationName}</green>`
    );
  }

  // Handle Gateway External ID change
  if (changes.external_id) {
    description.push(
      `Gateway external ID has been changed from <red>${changes.external_id.old}</red> to <green>${changes.external_id.new}</green>`
    );
  }

  // Handle Gateway type change
  if (changes.type) {
    description.push(
      `Gateway type has been updated from <red>${titleCase(
        changes.type.old
      )}</red> to <green>${titleCase(changes.type.new)}</green>`
    );
  }

  // Handle Gateway status change
  if (changes.status) {
    description.push(
      `Gateway status has been updated from <red>${titleCase(
        changes.status.old
      )}</red> to <green>${titleCase(changes.status.new)}</green>`
    );
  }

  // Handle Gateway organization change
  if (changes.organization_id && changed_organization_name) {
    description.push(
      `Gateway organization has been updated from <red>${changed_organization_name.old}</red> to <green>${changed_organization_name.new}</green>`
    );
  }

  return description.length > 0
    ? description
    : [`${titleCase(operation)} gateway ${name}`];
}

function formatDeviceForBO(log, operation) {
  const changes = log?.changes || {};
  const gateway_name = log?.gateway_name || {};
  const shortId = log?.data?.short_id || "Unknown";
  const disposition = log?.data?.disposition || "Unknown";
  const organizationName = log?.organizationName || null;
  const operationValue = log?.operation;

  let description = [];

  if (operationValue === 0) {
    description.push(
      `Device ${shortId} has been assigned to <green>${organizationName}</green>`
    );
  }

  // Handle short ID change
  if (changes.short_id) {
    description.push(
      `Device ${shortId} short ID has been changed from <red>${changes.short_id.old}</red> to <green>${changes.short_id.new}</green>`
    );
  }

  // Handle device name change
  if (changes.name) {
    description.push(
      `Device ${shortId} device name has been changed to <green>${changes.name.new}</green>`
    );
  }

  // Handle device status change
  if (changes.status) {
    description.push(
      `Device ${shortId} status has been changed to <green>${titleCase(
        changes.status.new
      )}</green>`
    );
  }

  // Handle disposition change
  if (changes.disposition) {
    description.push(
      `Device ${shortId} disposition has been changed from <red>${titleCase(
        changes.disposition.old
      )}</red> to <green>${titleCase(changes.disposition.new)}</green>`
    );
  }

  // Handle pre-approved return change
  if (changes.rma_request) {
    const rmaRequestValue = changes.rma_request.new || "N/A";
    description.push(
      `Device ${shortId} pre-approved return has been set as <green>${titleCase(
        rmaRequestValue
      )}</green>`
    );
  }

  // Handle notes addition
  if (changes.notes) {
    if (changes.notes.new === null) {
      description.push(`Notes have been cleared for device ${shortId}`);
    } else {
      description.push(
        `Notes "${changes.notes.new}" have been added to device ${shortId}`
      );
    }
  }

  // Handle gateway name change
  if (changes.gateway_id && gateway_name) {
    const oldName = gateway_name.old || "N/A";
    const newName = gateway_name.new || "N/A";

    description.push(
      `Gateway has been updated for device ${shortId} from <red>${oldName}</red> to <green>${newName}</green>`
    );
  }

  // Handle date_reported_missing change only if disposition is 'missing' and date_reported_missing is not null
  if (changes.date_reported_missing && disposition === "missing") {
    const dateReportedMissing = changes.date_reported_missing.new;
    if (dateReportedMissing) {
      description.push(
        `Device ${shortId} date reported missing on <green>${formatDate(
          dateReportedMissing
        )}</green>`
      );
    }
  }

  return description.length > 0
    ? description
    : [`${titleCase(operation)} device ${shortId}`];
}

function formatDeviceAssignmentForBO(log, operation) {
  let oldAndNewOrganization = log?.old_and_new_organization || {};
  const shortId = log?.data?.short_id || "Unknown";
  let description = [];

  // Handle device movement (facility change)
  if (oldAndNewOrganization.old && oldAndNewOrganization.new) {
    description.push(
      `Device ${shortId} moved to <green>${oldAndNewOrganization.new}</green> from <red>${oldAndNewOrganization.old}</red>`
    );
  }

  return description.length > 0
    ? description
    : [`${titleCase(operation)} device ${shortId}`];
}

// Format Organization
function formatOrganizationForBO(log, operation) {
  const oldAndNew = log?.old_and_new_organization || {};
  if (log?.data?.subtype === "unit" && (oldAndNew.old || oldAndNew.new)) {
    const unitName = log?.data?.name || "Unknown";
    if (oldAndNew.old && oldAndNew.new) {
      return [
        `Unit ${unitName} unassigned from <red>${oldAndNew.old}</red> and assigned to <green>${oldAndNew.new}</green>`,
      ];
    } else if (oldAndNew.new) {
      return [`Unit ${unitName} assigned to <green>${oldAndNew.new}</green>`];
    }
    return [`Unit ${unitName} unassigned from <red>${oldAndNew.old}</red>`];
  }

  // Support both log.new_data and log.data for operation_type and all fields
  const slackData =
    log?.new_data?.operation_type === "slack_integration_deletion"
      ? log.new_data
      : log?.data?.operation_type === "slack_integration_deletion"
      ? log.data
      : null;
  if (slackData) {
    const facilityType =
      slackData.facility_type || slackData.organization_type || slackData.type;
    const facilityName =
      slackData.facility_name || slackData.organization_name || slackData.name;
    const contactsDeleted = slackData.contacts_deleted || 0;

    if (facilityType === "facility") {
      return [
        `Slack integration was deleted for facility <red>${facilityName}</red>${
          contactsDeleted > 0
            ? ` (${contactsDeleted} contact${
                contactsDeleted > 1 ? "s" : ""
              } removed)`
            : ""
        }`,
      ];
    } else if (facilityType === "organization") {
      const facilitiesProcessed = slackData.facilities_processed || 0;
      const featuresCleared = slackData.features_cleared || 0;
      let details = [];
      if (featuresCleared > 0)
        details.push(
          `${featuresCleared} feature${featuresCleared > 1 ? "s" : ""} cleared`
        );
      if (contactsDeleted > 0)
        details.push(
          `${contactsDeleted} contact${contactsDeleted > 1 ? "s" : ""} removed`
        );
      if (facilitiesProcessed > 0)
        details.push(
          `${facilitiesProcessed} facilit${
            facilitiesProcessed > 1 ? "ies" : "y"
          } processed`
        );

      return [
        `Slack integration was deleted for organization <red>${facilityName}</red>${
          details.length > 0 ? ` (${details.join(", ")})` : ""
        }`,
      ];
    }
    return [`Slack integration was deleted for <red>${facilityName}</red>`];
  }

  // Check if changes object contains only allowed keys
  const keys = Object.keys(log?.changes);
  const hasOnlyUpdatedKeys = keys.every((key) => updateKeys.includes(key));
  if (hasOnlyUpdatedKeys) {
    return `${titleCase(operation)} ${titleCase(
      log?.organizationName
    )} details with no changes`;
  }
  const formattedLogs = formatChangeObjectToDescriptionForBO(
    log?.changes,
    ignoredKeys,
    log?.organizationType
  );
  return formattedLogs.split(",").filter(Boolean);
}

function formatChangeObjectToDescriptionForBO(
  changes,
  ignoreKeys = [],
  organizationType = null
) {
  let description = "";
  for (const key in changes) {
    if (changes.hasOwnProperty(key) && !ignoreKeys.includes(key)) {
      const change = changes[key];

      switch (key) {
        case "office_schedule":
        case "monitoring_schedule":
          description = formatTimeSchedule(change, ignoreKeys, key);
          break;
        case "property_management_system":
        case "pms_paused_at":
        case "unit_enrollment_mode":
          description += formatPMS(changes, change, key);
          break;
        case "night_agg_motion_config":
          description += formatNightAggMotionConfig(change, key);
          break;
        case "alerting_strategy":
          description += formatAlertingStrategy(change, key);
          break;
        case "location":
        case "shipping_address":
          description += formatLocationUpdate(change, key);
          break;
      }

      if (
        (typeof change.old === "object" || typeof change.new === "object") &&
        (change.old !== null || change.new !== null) &&
        !Array.isArray(change.old) &&
        !Array.isArray(change.new)
      ) {
        description += formatChangeObjectToDescriptionForBO(
          jsonDiff(change.new, change.old),
          ignoreKeys
        );
        continue;
      }

      if (duplicateLogsKey.includes(key)) continue;
      if (checkEquality(change.old, change.new)) continue;

      if (key === "time_zone" && organizationType !== "facility") continue;
      // format boolean logs
      if (typeof change.new === "boolean" || typeof change.old === "boolean") {
        description += `${titleCase(key)} has been ${
          change.new ? "turned on" : "turned off"
        },`;
      } else {
        description += `${titleCase(key)}- ${
          change.old !== undefined
            ? change.new !== undefined
              ? `changed from <red>${formatValue(
                  change.old
                )}</red> to <green>${formatValue(change.new)}</green>,`
              : `<red>${formatValue(change.old)}</red> has been removed,`
            : `<green>${formatValue(change.new)}</green> has been added,`
        }`;
      }
    }
  }
  return description;
}

function formatAlertingStrategy(change, parentKey) {
  if (!change.new || !change.old) {
    return `${titleCase(parentKey)} has been ${
      change.new ? "added" : "removed"
    },`;
  }

  return Object.entries(change.new)
    .filter(([key, newValue]) => newValue !== change.old[key])
    .map(([key, newValue]) => {
      const oldValue = change.old[key];
      if (key === "status") {
        return `${titleCase(parentKey)} has been ${newValue},`;
      }
      return `${titleCase(parentKey)} ${titleCase(key)} ${
        oldValue ? `changed from <red>${oldValue}</red> to ` : ""
      }<green>${newValue}</green>,`;
    })
    .join(" ");
}

function formatNightAggMotionConfig(change, key) {
  let description = "";
  description += `${titleCase(key)} ${
    change.new.disabled ? "disabled" : "enabled"
  } : Sensitivity ${change.new.sensitivity},`;

  return description;
}

function formatPMS(changes, change, key) {
  let description = "";
  if (key === "pms_paused_at") {
    description += `${titleCase(
      change["property_management_system"]
    )} sync has been paused,`;
  } else if (key === "unit_enrollment_mode") {
    const newMode = titleCase(changes["property_management_system"].new);
    const oldMode = titleCase(change.old);
    const newChange = titleCase(change.new);
    description += change.old
      ? `Pms sync mode changed from "${oldMode}" to "${newChange}",`
      : `${newMode} mode <green>"${newChange}"</green> has been added,`;
  } else if (change.new && !change.old) {
    description += `${titleCase(change.new)} sync has been turned on,`;
  } else {
    description += `${titleCase(change.old)} sync has been turned off,`;
  }
  return description;
}

function formatTimeSchedule(changes, ignoreKeys = [], parentKey) {
  let description = "";
  Object.entries(changes.new).forEach(([key, newValue]) => {
    if (
      !ignoreKeys.includes(key) &&
      JSON.stringify(newValue) !== JSON.stringify(changes.old[key])
    ) {
      description += [
        "alert_resumption_margin",
        "late_customer_margin",
      ].includes(key)
        ? `${titleCase(key)} changed from <red>${
            changes.old[key]
          }</red> to <green>${newValue}</green>,`
        : `${
            parentKey === "office_schedule" ? "Office" : "Access"
          } hours has been updated on ${titleCase(key)} from <red>"${
            changes.old[key].starts_at
          }" to "${changes.old[key].ends_at}"</red> to <green>"${
            newValue.starts_at
          }" to "${newValue.ends_at}"</green>,`;
    }
  });
  return description;
}

function formatLocationUpdate(changes, parentKey) {
  if (!changes) return "";

  const { new: newLocation, old: oldLocation } = changes;

  // Handle cases where entire new or old object is null
  if (newLocation === null && oldLocation !== null) {
    return `${titleCase(parentKey)} has been removed,`;
  }
  if (oldLocation === null && newLocation !== null) {
    return `${titleCase(parentKey)} has been added,`;
  }

  // If both are null, return empty string
  if (newLocation === null && oldLocation === null) {
    return "";
  }

  const descriptionParts = Object.keys({ ...newLocation, ...oldLocation })
    .filter((key) => {
      const oldValue = oldLocation?.[key];
      const newValue = newLocation?.[key];
      return oldValue !== newValue && (oldValue !== null || newValue !== null);
    })
    .map((key) => {
      const oldValue = oldLocation?.[key];
      const newValue = newLocation?.[key];
      if (oldValue === undefined && newValue !== null) {
        return `${titleCase(key)} <green>${formatValue(
          newValue
        )}</green> has been added,`;
      } else if (oldValue !== null && newValue === undefined) {
        return `${titleCase(key)} <red>${formatValue(
          oldValue
        )}</red> has been removed,`;
      } else {
        return `${titleCase(key)} changed from <red>${formatValue(
          oldValue
        )}</red> to <green>${formatValue(newValue)}</green>,`;
      }
    });

  return descriptionParts.length
    ? `${titleCase(parentKey)}- ${descriptionParts.join(" ")}`
    : "";
}

function formatBulkNotificationForBO(log, operation) {
  const data = log?.data || {};
  const newData = log?.new_data || {};
  let changes = log?.changes || {};
  if (typeof changes === "string") {
    try {
      changes = JSON.parse(changes);
    } catch (e) {
      changes = {};
    }
  }
  const operationValue = log?.operation;
  const facilityName =
    data?.workflow?.facility_name ||
    newData?.workflow?.facility_name ||
    log?.facilityName ||
    log?.organizationName ||
    "Unknown Facility";
  const workflowCount =
    data?.workflow?.workflow_count || newData?.workflow?.workflow_count || 0;
  const operationTypes =
    data?.workflow?.operation_types || newData?.workflow?.operation_types || [];
  const notes = data?.notes || newData?.notes || "";
  const status = data?.status || newData?.status || "";
  const workflowResolution =
    data?.workflow?.workflow_resolution ||
    newData?.workflow?.workflow_resolution ||
    "";
  const reason = data?.workflow?.reason || newData?.workflow?.reason || "";

  let description = [];

  if (operationValue === 0) {
    const operationTypesText =
      operationTypes.length > 0
        ? operationTypes
            .map((type) =>
              type.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())
            )
            .join(", ")
        : "";

    let desc = `Bulk workflow approval created for <green>${facilityName}</green>: <green>${workflowCount}</green> workflow${
      workflowCount !== 1 ? "s" : ""
    }`;

    if (operationTypesText) {
      desc += ` (${operationTypesText})`;
    }

    if (notes) {
      // Extract examples from notes if available
      const examplesMatch = notes.match(/Examples: (.+?)(?:\.\.\.|$)/);
      if (examplesMatch) {
        desc += `. Examples: ${examplesMatch[1]}`;
      }
    }

    description.push(desc);
  } else if (
    operationValue === 1 &&
    changes.status &&
    changes.status.new === "resolved"
  ) {
    if (workflowResolution === "rejected") {
      let desc = `Bulk workflow approval has been <red>rejected</red> for <green>${facilityName}</green>.Reason `;
      if (reason) {
        desc += `: ${reason}`;
      } else {
        desc += "Not given";
      }
      description.push(desc);
    } else if (workflowResolution === "approved") {
      description.push(
        `Bulk workflow approval has been <green>approved</green> for <green>${facilityName}</green>`
      );
    }
  }

  return description.length > 0
    ? description
    : [`${titleCase(operation)} bulk notification`];
}

function formatShipmentLogForBO(log, operation) {
  let changes = log?.changes || {};
  if (typeof changes === "string") {
    try {
      changes = JSON.parse(changes);
    } catch (e) {
      changes = {};
    }
  }

  let data = log?.data || log?.new_data || {};
  if (typeof data === "string") {
    try {
      data = JSON.parse(data);
    } catch (e) {
      data = {};
    }
  }

  const operationValue = log?.operation;
  const facilityName =
    log?.facilityName ||
    log?.facility_name ||
    log?.organizationName ||
    log?.organization_name ||
    "Unknown Facility";
  const actorName = log?.actorName || log?.actor_name || "Unknown User";
  const orderType = data?.order_type || "Unknown Order";
  const status = data?.status || "Unknown Status";

  const shipmentIdentifier = `${facilityName} Requested by ${actorName} 's with Order type ${orderType}`;

  let description = [];

  if (operationValue === 0) {
    const itemsText = formatShipmentItems(data?.items || []);
    const provisioningDevices = formatProvisioningDevices(
      data?.provisioning_devices || []
    );
    const notes = formatNotes(data?.notes || []);
    const trackingNumbers = formatTrackingNumbers(
      data?.tracking_numbers || { inbound: [], outbound: [] }
    );
    const dateShipped = data?.date_shipped
      ? formatDate(data.date_shipped)
      : null;
    const billable = data?.billable ? "billable" : "non-billable";
    const invoiced = data?.invoiced ? "invoiced" : "not invoiced";

    let details = [];
    details.push(`Order type: <green>${orderType}</green>`);
    details.push(`Status: <green>${titleCase(status)}</green>`);

    if (itemsText) {
      details.push(`Items: <green>${itemsText}</green>`);
    } else {
      details.push("without items");
    }

    if (provisioningDevices) {
      details.push(
        `Provisioning devices: <green>${provisioningDevices}</green>`
      );
    } else {
      details.push("without provisioning devices");
    }

    if (notes) {
      details.push(`Notes: <green>${notes}</green>`);
    } else {
      details.push("without notes");
    }

    if (trackingNumbers) {
      details.push(`Tracking numbers: <green>${trackingNumbers}</green>`);
    } else {
      details.push("without tracking numbers");
    }

    if (dateShipped) {
      details.push(`Date shipped: <green>${dateShipped}</green>`);
    } else {
      details.push("without date shipped");
    }

    details.push(`${titleCase(billable)}`);
    details.push(`${titleCase(invoiced)}`);

    return [
      `Shipment log created for <green>${facilityName}</green> Requested by <green>${actorName} 's</green> with ${details.join(
        ", "
      )}`,
    ];
  }

  if (changes.row_status) {
    if (changes.row_status.old === "A" && changes.row_status.new === "D") {
      description.push(
        `Shipment log <red>Deleted</red> for ${shipmentIdentifier}`
      );
    }
  }

  if (changes.status) {
    const oldStatus = titleCase(changes.status.old);
    const newStatus = titleCase(changes.status.new);
    description.push(
      `Shipment log status changed from <red>${oldStatus}</red> to <green>${newStatus}</green> for ${shipmentIdentifier}`
    );
  }

  if (changes.billable) {
    const billableStatus = changes.billable.new ? "billable" : "non-billable";
    description.push(
      `Shipment log marked as <green>${billableStatus}</green> for ${shipmentIdentifier}`
    );
  }

  if (changes.invoiced) {
    const invoicedStatus = changes.invoiced.new ? "invoiced" : "not invoiced";
    description.push(
      `Shipment log marked as <green>${invoicedStatus}</green> for ${shipmentIdentifier}`
    );
  }

  if (changes.notes) {
    const oldNotes = changes.notes.old || [];
    const newNotes = changes.notes.new || [];
    if (newNotes.length > oldNotes.length) {
      const addedNotes = newNotes.slice(oldNotes.length);
      const noteText = addedNotes
        .map((note) => `"${note.note_text}"`)
        .join(", ");
      description.push(
        `Notes added to shipment log for ${shipmentIdentifier}: ${noteText}`
      );
    }
  }

  if (changes.tracking_numbers) {
    const oldTracking = changes.tracking_numbers.old || {
      inbound: [],
      outbound: [],
    };
    const newTracking = changes.tracking_numbers.new || {
      inbound: [],
      outbound: [],
    };

    if (newTracking.outbound?.length > (oldTracking.outbound?.length || 0)) {
      const addedOutbound = newTracking.outbound.slice(
        oldTracking.outbound?.length || 0
      );
      const trackingNumbers = addedOutbound.map((t) => t.value).join(", ");
      description.push(
        `Outbound tracking number${
          addedOutbound.length > 1 ? "s" : ""
        } added to shipment log for ${shipmentIdentifier}: <green>${trackingNumbers}</green>`
      );
    }

    if (newTracking.inbound?.length > (oldTracking.inbound?.length || 0)) {
      const addedInbound = newTracking.inbound.slice(
        oldTracking.inbound?.length || 0
      );
      const trackingNumbers = addedInbound.map((t) => t.value).join(", ");
      description.push(
        `Inbound tracking number${
          addedInbound.length > 1 ? "s" : ""
        } added to shipment log for ${shipmentIdentifier}: <green>${trackingNumbers}</green>`
      );
    }
  }

  if (changes.provisioning_devices) {
    const oldDevices = changes.provisioning_devices.old || [];
    const newDevices = changes.provisioning_devices.new || [];

    if (newDevices.length > oldDevices.length) {
      const addedDevices = newDevices.slice(oldDevices.length);
      const deviceIds = addedDevices
        .map((d) => d.shortId || d.externalId || d.id)
        .join(", ");
      description.push(
        `Device${
          addedDevices.length > 1 ? "s" : ""
        } provisioned to shipment log for ${shipmentIdentifier}: <green>${deviceIds}</green>`
      );
    }
  }

  if (changes.billing_mode) {
    const oldMode = changes.billing_mode.old || "N/A";
    const newMode = changes.billing_mode.new || "N/A";
    description.push(
      `Billing mode changed from <red>${oldMode}</red> to <green>${newMode}</green> for ${shipmentIdentifier}`
    );
  }

  if (changes.date_shipped) {
    const shippedDate = changes.date_shipped.new;
    if (shippedDate) {
      description.push(
        `Shipment marked as shipped on <green>${formatDate(
          shippedDate
        )}</green> for ${shipmentIdentifier}`
      );
    }
  }

  return description.length > 0
    ? description
    : [`${titleCase(operation)} shipment log for ${shipmentIdentifier}`];
}

function formatShipmentItems(items) {
  if (!items || items.length === 0) return "";

  return items
    .map((item) => {
      const quantity = item.quantity || 0;
      const type = item.type || "Unknown";
      const description = item.description ? ` (${item.description})` : "";
      return `${quantity}x ${type}${description}`;
    })
    .join(", ");
}

function formatProvisioningDevices(devices) {
  if (!devices || devices.length === 0) return "";

  return devices
    .map(
      (device) =>
        device.shortId || device.externalId || device.id || "Unknown Device"
    )
    .join(", ");
}

function formatNotes(notes) {
  if (!notes || notes.length === 0) return "";

  return notes
    .filter((note) => note.row_status === "A" && note.note_text)
    .map((note) => `"${note.note_text}"`)
    .join(", ");
}

function formatTrackingNumbers(trackingNumbers) {
  if (!trackingNumbers) return "";

  const outbound = trackingNumbers.outbound || [];
  const inbound = trackingNumbers.inbound || [];

  let result = [];

  if (outbound.length > 0) {
    const outboundNumbers = outbound
      .filter((t) => t.row_status === "A" && t.value)
      .map((t) => t.value)
      .join(", ");
    if (outboundNumbers) {
      result.push(`Outbound: ${outboundNumbers}`);
    }
  }

  if (inbound.length > 0) {
    const inboundNumbers = inbound
      .filter((t) => t.row_status === "A" && t.value)
      .map((t) => t.value)
      .join(", ");
    if (inboundNumbers) {
      result.push(`Inbound: ${inboundNumbers}`);
    }
  }

  return result.join(", ");
}
